import { forwardRef } from "react";
import { IoTrophy } from "react-icons/io5";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { LeaguePlayerInfo } from "../LeaguePlayerInfo";
import { Thumbnails } from "../Thumbnails";
import { WatchLimitBlocker } from "../WatchLimitBlocker";
import { LeagueEmbeddablePlaylistViewModel } from "../../ViewModel";

import { Divider } from "MVVM/Components/Divider";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
} from "MVVM/State/embeddablePlaylist";
import EmbeddablePlayer from "MVVM/Views/EmbeddablePlayer";

export const LeagueEmbeddablePlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    handleMouseEnter: () => void;
  }
>(({ platform, playlist, handleMouseEnter }, ref) => {
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
  const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
  const blockedClip = useRecoilValue(BlockedClipState);

  const { handleWatchLimitReached, handleCloseBlocker } = useWatchLimit();

  LeagueEmbeddablePlaylistViewModel({
    playlist,
    platform,
  });

  return (
    <>
      <Flex flexDirection={"column"} style={{ height: "100%", width: "100%" }}>
        <StyledContainer onMouseEnter={handleMouseEnter}>
          <StyledGradientLayer className="gradient-layer">
            <StyledHeaderContainer>
              <Flex style={{ gap: "5px" }}>
                <StyledTrophyIcon />
                <Text variant="text.paragraphSmall" fontWeight={700}>
                  {activePlaylistClip?.title}
                </Text>
              </Flex>
              <Flex alignItems="center" style={{ gap: "4px" }}>
                <StyledPoweredByText variant="text.labelXXSmall">
                  Powered by
                </StyledPoweredByText>
                <Image
                  width={48}
                  height={10}
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/allstargg.png`}
                  onClick={() => {
                    const ajs_aid = window.rudderanalytics.getAnonymousId();
                    window.open(
                      `https://allstar.gg?ajs_aid=${ajs_aid}`,
                      "_blank",
                    );
                  }}
                />
              </Flex>
            </StyledHeaderContainer>
            <Divider mb={0} mt={0} />
            <StyledLeageWrapper>
              <FixedLeagueWrapper ref={ref} className="fixed-width-container">
                {activePlaylistClip ? (
                  <EmbeddablePlayer
                    className="player-component"
                    userRole="viewer"
                    onPlayEndVariant="nextUpPlaylist"
                  />
                ) : (
                  <PlayerLoadingSkeleton>
                    <LoadingSkeleton width="100%" height="100%" />
                  </PlayerLoadingSkeleton>
                )}
                {showWatchLimitBlocker && (
                  <WatchLimitBlocker
                    onClose={handleCloseBlocker}
                    playerName={blockedPlayerName}
                    clickedClip={blockedClip}
                    playlistId={playlist}
                    primaryColor="#3273FA"
                  />
                )}
              </FixedLeagueWrapper>
              <LeaguePlayerInfoWrapper>
                <LeaguePlayerInfo />
              </LeaguePlayerInfoWrapper>
            </StyledLeageWrapper>
            <Thumbnails
              clipsToDisplay={playlistClips}
              renderLolPlaylist
              onWatchLimitReached={handleWatchLimitReached}
            />
          </StyledGradientLayer>
        </StyledContainer>
      </Flex>
    </>
  );
});

const StyledLeageWrapper = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 12px;
  min-height: 243px;
  width: 100%;
`;

const FixedLeagueWrapper = styled(Box)`
  flex-shrink: 0;

  position: relative;
  width: 432px;
  z-index: 1;

  &:not(:has(:fullscreen)) {
    .vjs-tech {
      border-radius: 8px;
      height: auto !important;
      width: 100% !important;
    }
    .video-js {
      background: transparent;
      width: 100% !important;
      height: 100% !important;
      min-height: 243px;
    }
  }

  .player-component {
    position: relative;
  }
`;

const LeaguePlayerInfoWrapper = styled(Box)`
  align-items: center;
  display: flex;
  flex: 1 1 0%;
  height: 100%;
  justify-content: center;
`;
const StyledPoweredByText = styled(Text)`
  color: #cddcfe;
`;
const StyledTrophyIcon = styled(IoTrophy)`
  color: rgba(50, 115, 250, 1);
  height: 20px;
  width: 20px;
`;

const StyledHeaderContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledGradientLayer = styled(Flex)`
  backdrop-filter: blur(6px);
  background: linear-gradient(
      227.35deg,
      rgba(49, 113, 138, 0.3) -3.85%,
      rgba(99, 173, 146, 0.3) 100%
    ),
    linear-gradient(0deg, rgba(0, 0, 0, 0.87), rgba(0, 0, 0, 0.87));
  flex-direction: column;
  gap: 12px;
  height: 100%;
  padding: 10px;
  width: 100%;

  background: none;
  @media (max-width: 502px) {
    gap: 8px;
    padding: 8px;
  }

  @media (max-width: 425px) {
    padding: 6px;
  }
`;

const StyledContainer = styled(Flex)`
  background-image: url(${process.env
    .REACT_APP_S3_CLIP_BUCKET}/static/web/playlistBackgroundImage.jpg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;

  border: 2px solid rgba(50, 115, 250, 0.4);
  border-radius: 6px;
  background: linear-gradient(rgba(26, 36, 74, 0.85), rgba(26, 36, 74, 0.85)),
    url(${process.env
      .REACT_APP_S3_CLIP_BUCKET}/static/web/lolPlaylistBackgroundImage.png);
  @media (max-width: 502px) {
    .fixed-width-container {
      height: 207px;
      width: 368px;
    }
  }

  @media (max-width: 425px) {
    .fixed-width-container {
      height: 185px;
      width: 330px;
    }
  }

  @media (max-width: 380px) {
    .fixed-width-container {
      height: 162px;
      width: 288px;
    }
  }
`;
